/**
 * Session store · решение 45: короткоживущий bearer в памяти приложения
 * с восстановлением через sessionStorage. localStorage ЗАПРЕЩЁН.
 */

/**
 * Форма пользователя — по РЕАЛЬНОМУ ответу auth.login (проверен вживую):
 * data.user = { id, login, profile: { name }, roles[], capabilities[] }.
 * displayName в ответе НЕТ — имя берётся из profile.name с фолбэком на login.
 */
export interface SessionUser {
  id: number;
  login: string;
  /**
   * employee_profile из базы: сервер кладёт как минимум { name }, у портальных
   * учёток ещё { kind: 'portal' }, а карточка цеха может добавить специализацию
   * и код. Тип открытый — портал читает свои поля, не ломая остальных.
   */
  profile?: ({ name?: string } & Record<string, unknown>) | null;
  roles: string[];
  capabilities: string[];
}

export interface SessionState {
  token: string | null;
  user: SessionUser | null;
  /** Срок годности bearer (ISO), если сервер прислал; информативно */
  expiresAt: string | null;
}

/** Отображаемое имя: user.profile.name → фолбэк на user.login. */
export function displayName(user: SessionUser | null): string {
  if (!user) return '';
  return user.profile?.name?.trim() || user.login;
}

const STORAGE_KEY = 'wg.erp.session.v1';
const EMPTY: SessionState = { token: null, user: null, expiresAt: null };

export interface LoginArgs {
  token: string;
  user: SessionUser | null;
  expiresAt?: string | null;
}

type Listener = () => void;

class SessionStore {
  private state: SessionState = EMPTY;
  private listeners = new Set<Listener>();

  /** Восстановление после reload вкладки (sessionStorage — per-tab). */
  restore(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<SessionState>;
      if (typeof parsed.token === 'string' && parsed.token.length > 0) {
        // Истёкшая сессия не восстанавливается — сразу экран входа, без «мигания» ошибок.
        if (parsed.expiresAt && Date.parse(parsed.expiresAt) < Date.now()) {
          sessionStorage.removeItem(STORAGE_KEY);
          this.state = EMPTY;
          return;
        }
        this.state = {
          token: parsed.token,
          user: parsed.user ?? null,
          expiresAt: parsed.expiresAt ?? null,
        };
        this.emit();
      }
    } catch {
      // повреждённое хранилище — начинаем без сессии
      this.state = EMPTY;
    }
  }

  login({ token, user, expiresAt = null }: LoginArgs): void {
    this.state = { token, user, expiresAt };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // приватный режим без quota — сессия живёт только в памяти
    }
    this.emit();
  }

  /** Обновить данные о себе в живой сессии (сменили имя в кабинете — меню и шапка показывают новое сразу). */
  setUser(user: SessionUser): void {
    if (!this.state.token) return;
    this.login({ token: this.state.token, user, expiresAt: this.state.expiresAt });
  }

  /** Причина последнего сброса (показывается на экране входа; очищается при чтении). */
  private lastClearReason: 'expired' | 'logout' | null = null;
  takeClearReason(): 'expired' | 'logout' | null {
    const r = this.lastClearReason;
    this.lastClearReason = null;
    return r;
  }

  /** Сброс сессии (logout или ответ `unauthenticated`). */
  clear(reason: 'expired' | 'logout' = 'logout'): void {
    this.lastClearReason = this.state.token ? reason : null;
    this.state = EMPTY;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    this.emit();
  }

  getToken(): string | null {
    return this.state.token;
  }

  getUser(): SessionUser | null {
    return this.state.user;
  }

  isAuthenticated(): boolean {
    return this.state.token !== null;
  }

  /** Для useSyncExternalStore */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SessionState => this.state;

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

export const sessionStore = new SessionStore();
